<template>
  <Form as="" ref="form" :validation-schema="validationSchema">
    <div class="edit-address-form">
      <BackstageInput
        v-model="addEditDeliveryAddress.name"
        autocomplete="name"
        name="delivery_name"
        required
        tab-index="1"
        data-cy="delivery-name-box"
        data-cy-input="delivery-name"
        :label="$t('viewOrder.delivery_name')" />
      <BackstageInput
        v-model="addEditDeliveryAddress.street"
        autocomplete="street-address"
        name="street_address"
        data-cy="street-address-box"
        data-cy-input="street-address"
        required
        tab-index="2"
        :label="$t('viewOrder.street_address')" />
      <BackstageInput
        v-if="streetDescriptionEnabled"
        v-model="addEditDeliveryAddress.streetDescription"
        name="street_address_description"
        data-cy="street-address-description-box"
        data-cy-input="street-address-description"
        autocomplete="street_address_description"
        tab-index="3"
        :label="$t('viewOrder.street_address_description')" />
      <div class="fields-flex-new">
        <BackstageInput
          v-model="addEditDeliveryAddress.city"
          autocomplete="address-level2"
          name="city"
          data-cy="city-box"
          data-cy-input="city"
          required
          tab-index="4"
          :label="$t('viewOrder.city')" />
        <BackstageInput
          v-model="addEditDeliveryAddress.zipCode"
          autocomplete="postal-code"
          name="post_code"
          data-cy="post-code-box"
          data-cy-input="post-code"
          required
          tab-index="5"
          :label="$t('viewOrder.post_code')" />
      </div>
      <div v-if="statesSelectEnabled" class="select-state">
        <BackstageInputLabel :label="$t('viewOrder.state')" for-label="state" required>
          <Field
            as="select"
            v-model="addEditDeliveryAddress.state"
            tab-index="6"
            name="state"
            data-cy="state-box"
            data-cy-input="state"
            autocomplete="address-level1"
            class="with-arrow">
            <option v-for="state in states()" :key="state.code" :value="state.code">
              {{ state.name }}
            </option>
          </Field>
        </BackstageInputLabel>
      </div>
      <BackstageInput v-model="addEditDeliveryAddress.country" :label="$t('viewOrder.country')" :disabled="true" />
      <div class="fields-flex-new buttons">
        <BackstageButton
          tab-index="7"
          :variant="ButtonVariant.SECONDARY"
          data-cy="cancel-button-cnt"
          data-cy-button="cancel-button"
          v-on:click="$emit('cancel')">
          {{ $t('global.cancel_button') }}
        </BackstageButton>
        <BackstageButton
          tab-index="6"
          :disabled="!formIsValid"
          data-cy="save-button-cnt"
          data-cy-button="save-button"
          v-on:click="save()">
          {{ $t('global.save_button') }}
        </BackstageButton>
      </div>
    </div>
  </Form>
</template>

<script lang="ts">
import { useBPLogger } from '@/composables/business-process-logger';
import { Address } from '@/models/address.model';
import { ButtonVariant } from '@/models/button-variant.enum';
import structuredClone from '@ungap/structured-clone';
import { storeToRefs } from 'pinia';
import { Field, Form } from 'vee-validate';
import { defineComponent } from 'vue';
import { object, string, type AnyObjectSchema, type ObjectShape } from 'yup';
import { toRawDeep } from '../../composables/to-raw-deep';
import { countriesWithStates, type State } from '../../constants/global.constants';
import { useUserStore } from '../../stores/user';
import BackstageButton from '../common/BackstageButton.vue';
import BackstageInput from '../common/BackstageInput.vue';
import BackstageInputLabel from '../common/BackstageInputLabel.vue';

export default defineComponent({
  name: 'add-edit-delivery-address',
  components: {
    BackstageButton,
    BackstageInput,
    BackstageInputLabel,
    Field,
    // eslint-disable-next-line vue/no-reserved-component-names
    Form,
  },
  props: {
    deliveryAddress: {
      type: Object,
      default: undefined,
    },
    isPickupAddress: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { submitShippingAddressBPLog } = useBPLogger();

    return {
      ButtonVariant,
      submitShippingAddressBPLog,
    };
  },
  data() {
    const { selectedAccount } = storeToRefs(useUserStore());
    let addEditDeliveryAddress = structuredClone(toRawDeep(this.deliveryAddress)) as Address;

    if (!addEditDeliveryAddress) {
      addEditDeliveryAddress = new Address();
      addEditDeliveryAddress.country = selectedAccount.value?.billingCountry;
    } else {
      (this.$refs as any).form?.validate();
    }

    return { addEditDeliveryAddress, account: selectedAccount, formIsValid: false };
  },
  computed: {
    validationSchema(): AnyObjectSchema {
      let schema: ObjectShape = {
        delivery_name: string().required(
          this.$t('formsValidations.rules_error_messages.required', {
            field: this.$t('formsValidations.fields.delivery_name'),
          }),
        ),
        street_address: string().required(
          this.$t('formsValidations.rules_error_messages.required', {
            field: this.$t('formsValidations.fields.street_address'),
          }),
        ),
        city: string().required(
          this.$t('formsValidations.rules_error_messages.required', {
            field: this.$t('formsValidations.fields.city'),
          }),
        ),
        post_code: string().required(
          this.$t('formsValidations.rules_error_messages.required', {
            field: this.$t('formsValidations.fields.post_code'),
          }),
        ),
      };

      if (this.statesSelectEnabled) {
        schema.state = string().required(
          this.$t('formsValidations.rules_error_messages.required', {
            field: this.$t('formsValidations.fields.state'),
          }),
        );
      }

      return object(schema);
    },
    statesSelectEnabled(): boolean {
      return this.states().length > 0;
    },
    streetDescriptionEnabled(): boolean {
      return this.isPickupAddress;
    },
  },
  watch: {
    selectedAccount(newVal) {
      if (newVal) {
        this.addEditDeliveryAddress.country = newVal.billingCountry;
      }
    },
    addEditDeliveryAddress: {
      handler() {
        this.validateForm();
      },
      deep: true,
    },
  },
  methods: {
    states(): State[] {
      const selectedCountry = this.account?.billingCountry?.toLowerCase();
      if (!Object.keys(countriesWithStates).includes(selectedCountry)) {
        return [];
      }

      return countriesWithStates[selectedCountry as keyof typeof countriesWithStates];
    },
    save() {
      if (this.formIsValid) {
        this.$emit('delivery-address-updated', this.addEditDeliveryAddress);
        console.log(
          this.submitShippingAddressBPLog(true, {
            subflow: 'update_delivery_address',
          }),
        );
      }
    },
    async validateForm() {
      this.formIsValid = (await (this.$refs as any).form?.validate({ mode: 'silent' }))?.valid;
    },
  },
});
</script>

<style lang="scss" scoped>
.edit-address-form {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 15px;

  span {
    width: 100%;
  }

  .with-arrow {
    width: 100%;
  }

  .fields-flex-new {
    justify-content: space-between;
    width: 100%;
    gap: 12px;

    &.buttons {
      justify-content: flex-end;
    }

    span {
      width: 100%;
    }
  }
  .select-state {
    display: block;
    width: 100%;
  }

  .validation-error {
    text-transform: lowercase;
  }
}
</style>
